import React, { FC, useMemo, useState } from 'react';
import { Box, makeStyles, Typography } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import useRootSelector from 'common/hooks/useRootSelector';
import { useNavigate } from 'react-router-dom';
import AddClockInDataModal from 'modules/timeclock/components/AddClockInDataModal/AddClockInDataModal';
import AddUnitDataModal from 'modules/timeclock/components/AddUnitDataModal/AddUnitDataModal';
import TimeclockHistoryTable from './TimeclockHistoryTable';
import TimeclockHistoryModal from './TimeclockHistoryModal';
import { ReactComponent as AddIcon } from 'img/icons/add_deprecated.svg';
import { ActionItem, ActionsMenu, Button } from 'common/components/index';
import { ITimeclockListItem } from 'modules/timeclock/interfaces/timeclock';
import { CustomTheme } from 'common/ui/interfaces';
import { Routes } from 'constants/route.constants';
import {
  selectMyProfileTimeclockHistory,
  selectMyProfileTimeclockHistoryIsLoading,
} from 'modules/my-profile/state/profile/selectors';
import { selectCurrentUser } from 'modules/authentication/state/selectors';
import messages from 'common/components/EmployeeProfile/messages';
import permissionsMessages from 'modules/permissions/messages/messages';
import timeclockMessages from 'modules/timeclock/messages';
import usePermissionContext from 'common/hooks/context/usePermissionContext';
import { IEmployeeProfileDto } from 'common/interfaces/employee';
import { IUserProfileInfoImt } from 'modules/authentication/interfaces';

const useStyles = makeStyles((theme: CustomTheme) => ({
  block: {
    width: '100%',
    border: `1px solid ${theme.palette.borderColor?.main}`,
    borderRadius: '3px',
  },
  blockActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  blockTitle: {
    textTransform: 'uppercase',
    padding: theme.spacing(2, 0, 2, 0),
  },
}));

interface ITimeclockHistoryBlock {
  recentTimeclockHistory: ITimeclockListItem[];
  isLoading: boolean;
  profile: IEmployeeProfileDto;
}

const TimeclockHistoryBlock: FC<ITimeclockHistoryBlock> = ({
  recentTimeclockHistory,
  isLoading,
  profile,
}) => {
  const navigate = useNavigate();
  const classes = useStyles();
  const {
    TIME_CLOCK_IN,
    TIME_CLOCK_VIEW,
    TIME_CLOCK_VIEW_OWN,
    TIME_CLOCK_PAYROLL_REPORT_VIEW,
    TIME_CLOCK_PAYROLL_REPORT_VIEW_OWN,
  } = usePermissionContext();

  const [isTimeclockHistoryModalOpen, setIsTimeclockHistoryModalOpen] = useState(false);
  const [isAddUnitDataModalOpen, setIsAddUnitDataModalOpen] = useState(false);
  const [isAddClockInDataModalOpen, setIsAddClockInDataModalOpen] = useState(false);

  const currentEmployee: IUserProfileInfoImt = useRootSelector(selectCurrentUser);
  const myProfileTimeclockHistory = useRootSelector(selectMyProfileTimeclockHistory);
  const timeclockHistoryLoading = useRootSelector(selectMyProfileTimeclockHistoryIsLoading);

  const timeclockHistoryList = useMemo(() => myProfileTimeclockHistory?.toJS(), [
    myProfileTimeclockHistory,
  ]);

  const timeclockViewHasPermission = useMemo(
    () => TIME_CLOCK_VIEW || (TIME_CLOCK_VIEW_OWN && profile.id === currentEmployee.get('id')),
    [TIME_CLOCK_VIEW, TIME_CLOCK_VIEW_OWN, profile.id, currentEmployee],
  );

  const timeclockPayrollReportViewHasPermission = useMemo(
    () =>
      TIME_CLOCK_PAYROLL_REPORT_VIEW ||
      (TIME_CLOCK_PAYROLL_REPORT_VIEW_OWN && profile.id === currentEmployee.get('id')),
    [
      TIME_CLOCK_PAYROLL_REPORT_VIEW,
      TIME_CLOCK_PAYROLL_REPORT_VIEW_OWN,
      profile.id,
      currentEmployee,
    ],
  );

  return (
    <Box
      className={classes.block}
      pr={2}
      pl={2}
      pb={timeclockViewHasPermission ? 2.5 : undefined}
      minWidth="50%"
    >
      <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5" color="textSecondary" className={classes.blockTitle}>
          <FormattedMessage {...messages.timeclockHistory} />
        </Typography>

        <Box className={classes.blockActions}>
          {timeclockPayrollReportViewHasPermission ? (
            <Button
              onClick={() => navigate(Routes.MY_PAYROLL_REPORT)}
              hasHoverStyle
              color="primary"
            >
              <FormattedMessage {...timeclockMessages.payrollReportLabel} />
            </Button>
          ) : null}

          {timeclockViewHasPermission ? (
            <Button
              onClick={() => setIsTimeclockHistoryModalOpen(true)}
              hasHoverStyle
              color="primary"
            >
              <FormattedMessage {...permissionsMessages.viewAll} />
            </Button>
          ) : null}

          <Box>
            <ActionsMenu
              horizontal
              icon={
                <Button hasHoverStyle color="primary">
                  <AddIcon />
                </Button>
              }
            >
              {TIME_CLOCK_IN ? (
                <ActionItem key="1" onClick={() => setIsAddClockInDataModalOpen(true)}>
                  <FormattedMessage {...timeclockMessages.clockInOption} />
                </ActionItem>
              ) : null}

              <ActionItem key="2" onClick={() => setIsAddUnitDataModalOpen(true)}>
                <FormattedMessage {...timeclockMessages.unitOption} />
              </ActionItem>
            </ActionsMenu>
          </Box>
        </Box>
      </Box>

      {timeclockViewHasPermission ? (
        <TimeclockHistoryTable
          disableSorting
          timeclockHistory={recentTimeclockHistory}
          isTimeclockHistoryLoading={isLoading}
          hidePagination
        />
      ) : null}

      {isTimeclockHistoryModalOpen && (
        <TimeclockHistoryModal
          isOpen={isTimeclockHistoryModalOpen}
          onClose={() => setIsTimeclockHistoryModalOpen(false)}
          timeclockHistory={timeclockHistoryList}
          isTimeclockHistoryLoading={timeclockHistoryLoading}
        />
      )}

      {isAddClockInDataModalOpen && (
        <AddClockInDataModal
          isOpen={isAddClockInDataModalOpen}
          employeeId={currentEmployee.get('id')}
          onClose={() => setIsAddClockInDataModalOpen(false)}
          isProfile
        />
      )}

      {isAddUnitDataModalOpen && (
        <AddUnitDataModal
          isOpen={isAddUnitDataModalOpen}
          employeeId={currentEmployee.get('id')}
          onClose={() => setIsAddUnitDataModalOpen(false)}
          isProfile
        />
      )}
    </Box>
  );
};

export default TimeclockHistoryBlock;
